import { map, tap } from 'rxjs/operators';
import { CustomerContractModel } from '../models/customer-contract-model';
import { CommonLookupItem } from '../../../shared/models/common-lookup-item.model';
import { CustomerAgentDetailsModel } from '../models/customer-agent-details-model';
import { UserModel } from '../../../core/user-model.model';

import { ServerValidationResult } from '../../../shared/validation/server-validation-result';
import { EmptyData } from '../../../core/models/empty-data';
import { CustomerModel } from '../models/customer-model';
import { NewPagingWrapper } from '../../../core/models/paging-wrapper';
import { Observable } from 'rxjs';
import { CustomerFilterModel } from '../models/customer-filter-model';
import { PagingFilter } from '../../../shared/models/paging-filter.model';
import { UrlConfigContainer } from '../../../core/url-config';
import { AjaxDataServiceService } from '../../../core/ajax-data-service.service';
import { Injectable } from '@angular/core';
import { ActionResultModel } from '../../../shared/models/action-result-model';
import { CustomerAddEventModel } from '../models/customer-add-event-model';
import { CustomerAgentSaveResultModel } from '../models/customer-agent-save-result-model';
import { CustomerAgentDeleteResultModel } from '../models/customer-agent-delete-result-model';
import { CustomerBrandingModel } from '../models/customer-branding-model';
import { SmartHttpService } from '@admin/core/smart-http.service';
import { ApiResponse } from '@admin/core/api-response';
import { CustomerModerationViewModel } from '../models/customer-moderation.model';
import { NewAjaxDataServiceService } from '@admin/core/new-ajax-data.service';
import { UrlConfigService } from '@hrra/core';
import { CvBoxChannel, CvBoxChannelViewModel } from '../models/cv-box-channels.model';

@Injectable()
export class CustomerService {
  public getCustomer(customerId: number): Observable<CustomerModel> {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.customer.get, {
      id: customerId,
    });
    return this.ajax.getData(
      url,
      { customerId: customerId.toString() },
      CustomerModel
    );
  }

  public listCustomer(
    filter: CustomerFilterModel
  ): Observable<ApiResponse<NewPagingWrapper<CustomerModel>>> {
    let result = {
      Status: filter.status,
      TaxCode: filter.taxCode,
      Name: filter.name,
      Agent: filter.agent,
      ContactPersonEmail: filter.contactPersonEmail,
      RegistrationDate: filter.registrationDate,
      StatusId: filter.statusId,
      PortfolioId: filter.portfolioId,
      Start: filter.start,
      Limit: filter.limit,
      SortParameters: filter.sortParameters,
    };
    return this.ajaxNew.postDataPaging<CustomerModel>(
      this.urlService.urlConfig.api.data.customer.list,
      result
    );
  }
  public getModerationCustomer(
    customerId: number
  ): Observable<ApiResponse<CustomerModerationViewModel>> {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.getModerationCustomer,
      { customerId: customerId }
    );
    return this.ajaxNew.getData(url);
  }

  public getModerationCount() {
    let url = this.urlService.urlConfig.api.data.customer.getModerationCountStat;
    return this.ajaxNew.getData(url);
  }

  public getCvBoxChannels(customerId: number) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.customer.getCvBoxChannels, {customerId: customerId});
    return this.ajaxNew.getData<ApiResponse<CvBoxChannelViewModel>>(url);
  }

  public saveCvBoxChannels(customerId: number, websites: CvBoxChannelViewModel) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.customer.postCvBoxChannels, {customerId: customerId});
    return this.ajaxNew.postData(url, websites);
  }

  public approveCustomerModeration(customerModerationId) {
    return this.ajaxNew.postData(
      this.urlService.format(
        this.urlService.urlConfig.api.data.customer.approveCustomerModeration,
        { customerModerationId: customerModerationId }
      ),
      {}
    );
  }

  public rejectCustomerModeration(customerModerationId) {
    return this.ajaxNew.postData(
      this.urlService.format(
        this.urlService.urlConfig.api.data.customer.rejectCustomerModeration,
        { customerModerationId: customerModerationId }
      ),
      {}
    );
  }

  public saveCustomerDetails(customer: CustomerModel) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.customer.details.save, {
      id: customer.customerId,
    });
    let data = customer.toObject();
    return this.ajax.postData(url, data, EmptyData);
  }

  public saveCustomerBilling(customer: CustomerModel) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.customer.billing.save, {
      id: customer.customerId,
    });
    let data = customer.toObject();
    return this.ajax.postData(url, data, EmptyData);
  }

  public saveCustomerAgent(customerAgent: CustomerAgentDetailsModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.agents.save,
      {}
    );
    let data = customerAgent.toObject();
    return this.ajax.postData(url, data, CustomerAgentSaveResultModel);
  }

  public validateIdNumber(customer: CustomerModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.billing.validate,
      {}
    );
    let data = {
      entity: 'IdNumber',
      data: customer.idNumber,
    };
    return this.ajax.postData(url, data, ServerValidationResult).toPromise();
  }

  public validateEmail(email: string) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.agents.validate,
      {}
    );
    let data = {
      entity: 'Email',
      data: email,
    };
    return this.ajax.postData(url, data, ServerValidationResult).toPromise();
  }

  public getTemporaryPassword(customerAgentId: number) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.agents.resetPassword,
      { id: customerAgentId }
    );
    return this.ajax.postData(url, {}, UserModel);
  }

  public getActivationLink(customerAgentId: number) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.agents.activationLink,
      { id: customerAgentId }
    );
    return this.ajax.postData(url, {}, CustomerModel);
  }

  public activateAgent(customerAgent: CustomerAgentDetailsModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.agents.activate,
      {}
    );
    let data = customerAgent.toObject();
    return this.ajax.postData(url, data, EmptyData);
  }

  public deactivateAgent(customerAgent: CustomerAgentDetailsModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.agents.deactivate,
      {}
    );
    let data = customerAgent.toObject();
    return this.ajax.postData(url, data, EmptyData);
  }

  public deleteAgent(customerAgent: CustomerAgentDetailsModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.agents.delete,
      {}
    );
    let data = customerAgent.toObject();
    return this.ajax.postData(url, data, CustomerAgentDeleteResultModel);
  }

  public listCustomerByName(
    customerName: string
  ): Observable<CommonLookupItem<any, any>[]> {
    let data = {
      autoCompleteText: customerName,
    };
    return <Observable<CommonLookupItem<any, any>[]>>(
      this.ajax
        .getDataPaging(
          this.urlService.urlConfig.api.data.customer.listByName,
          data,
          CommonLookupItem
        )
        .pipe(map((c) => c.items))
    );
  }

  public saveCustomerContract(customerContract: CustomerContractModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.contracts.save,
      {}
    );
    let data = customerContract.toObject();
    return this.ajax.postData(url, data, EmptyData);
  }

  public deleteCustomerContract(customerContractId: number) {
    const payload = {
      customerContractId,
    };
    return this.ajax.postData(
      this.urlService.urlConfig.api.data.customer.contracts.delete,
      payload,
      EmptyData
    );
  }

  public deleteCustomer(customerId: number) {
    return this.ajax.postData(
      this.getCustomerDeleteUrl(customerId),
      EmptyData,
      ActionResultModel
    );
  }

  public archiveCustomer(customerId: number) {
    return this.ajax.postData(
      this.getCustomerArchiveUrl(customerId),
      EmptyData,
      ActionResultModel
    );
  }

  public deleteCustomerEvent(customerId: number, eventId: number) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.customer.events.delete, {
      id: customerId,
    });
    return this.ajax.postData(url, { eventId }, ActionResultModel);
  }

  public listActiveEvents(): Observable<CommonLookupItem<any, any>[]> {
    return <Observable<CommonLookupItem<any, any>[]>>(
      this.ajax
        .getDataPaging(
          this.urlService.urlConfig.api.data.customer.events.list,
          null,
          CommonLookupItem
        )
        .pipe(map((r) => r.items))
    );
  }

  public listEventPackages(eventId: number) {
    return this.ajax
      .getDataPaging(
        this.urlService.urlConfig.api.data.customer.events.listPackages,
        { eventId: eventId },
        CommonLookupItem
      )
      .pipe(map((r) => r.items));
  }

  public addEvent(event: CustomerAddEventModel) {
    return this.ajax.postData(
      this.urlService.urlConfig.api.data.customer.events.add,
      event,
      ActionResultModel
    );
  }

  public requestCvDatabaseAccess(
    eventParticipantId: number,
    storeItemId: number
  ) {
    return this.ajax.postData(
      this.urlService.urlConfig.api.data.customer.events.requestCvDatabaseAccess,
      { EventParticipantId: eventParticipantId, StoreItemId: storeItemId },
      ActionResultModel
    );
  }

  private getCustomerDeleteUrl(customerId: number) {
    return this.urlService.format(this.urlService.urlConfig.api.data.customer.delete, {
      id: customerId,
    });
  }

  private getCustomerArchiveUrl(customerId: number) {
    return this.urlService.format(this.urlService.urlConfig.api.data.customer.archive, {
      id: customerId,
    });
  }

  public createCustomerBranding(customerBranding: CustomerBrandingModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.branding.create,
      {}
    );
    let data = customerBranding.toObject();
    return this.ajax.postData(url, customerBranding, ActionResultModel);
  }

  public updateCustomerBranding(customerBranding: CustomerBrandingModel) {
    let url = this.urlService.format(
      this.urlService.urlConfig.api.data.customer.branding.save,
      {}
    );
    let data = customerBranding.toObject();
    return this.ajax.postData(url, customerBranding, ActionResultModel);
  }

  constructor(
    private ajax: AjaxDataServiceService,
    private ajaxNew: NewAjaxDataServiceService,
    private urlService: UrlConfigService<UrlConfigContainer>
  ) {}
}
